import React from 'react';

export function StarterPreservation() {
  return (
    <div className="space-y-6 text-white/90" style={{ userSelect: "none" }}>
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Preserving Your Sourdough Starter</h3>
        <p className="mb-4">
          Knowing how to properly preserve your sourdough starter is essential for maintaining its health during periods when you're not actively baking. Whether you're going on vacation, taking a break from baking, or simply want a backup of your starter, these preservation methods will help ensure your starter remains viable.
        </p>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Short-Term Storage (1-2 Weeks)</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Refrigeration Method</h4>
            <p className="text-sm text-white/80">
              The refrigerator is the most common way to store a starter for short periods without daily feeding.
            </p>
            <ol className="list-decimal list-inside text-sm text-white/80 mt-2 space-y-2">
              <li>Feed your starter and let it rise for 1-2 hours at room temperature</li>
              <li>Place in a clean container with a loose-fitting lid (to allow gases to escape)</li>
              <li>Store in the refrigerator (ideally at 38-40°F/3-4°C)</li>
              <li>Can be stored this way for 1-2 weeks without feeding</li>
              <li>When ready to use, remove from refrigerator and feed once or twice before baking</li>
            </ol>
          </div>
          
          <div className="bg-amber-500/10 p-4 rounded-lg">
            <h4 className="text-amber-400 font-medium mb-2">Pro Tips for Refrigeration</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Feed your starter before refrigerating, not after removing from the refrigerator</li>
              <li>Use a container at least 3 times the volume of your starter to allow for expansion</li>
              <li>A thin layer of hooch (dark liquid) may form on top—this is normal and can be stirred back in or poured off</li>
              <li>Allow 24-48 hours of regular feeding to revive refrigerated starter before baking</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Long-Term Storage (Months to Years)</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Dehydration Method</h4>
            <p className="text-sm text-white/80">
              Dehydrating your starter creates a backup that can be stored for years and easily reactivated when needed.
            </p>
            <ol className="list-decimal list-inside text-sm text-white/80 mt-2 space-y-2">
              <li>Feed your starter and wait until it's at peak activity (doubled in size)</li>
              <li>Spread a thin layer (about 1/8 inch) on parchment paper or a silicone mat</li>
              <li>Allow to dry completely at room temperature (24-48 hours) until brittle</li>
              <li>Break into small pieces and store in an airtight container</li>
              <li>Keep in a cool, dark place or freezer for extended storage</li>
            </ol>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Reactivating Dried Starter</h4>
            <ol className="list-decimal list-inside text-sm text-white/80 mt-2 space-y-2">
              <li>Grind dried starter pieces into powder (optional)</li>
              <li>Combine 10g dried starter with 50g water and 50g flour</li>
              <li>Cover loosely and let sit at room temperature for 24 hours</li>
              <li>Discard half and feed with equal parts flour and water</li>
              <li>Repeat feedings every 12 hours until starter is active (usually 3-5 days)</li>
            </ol>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Freezing Method</h4>
            <p className="text-sm text-white/80">
              Freezing is another option for long-term storage, though some bakers prefer dehydration.
            </p>
            <ol className="list-decimal list-inside text-sm text-white/80 mt-2 space-y-2">
              <li>Feed starter and wait until it's at peak activity</li>
              <li>Place in a freezer-safe container with room for expansion</li>
              <li>Freeze for up to 1 year</li>
              <li>To revive, thaw at room temperature for 24 hours, then begin regular feeding schedule</li>
            </ol>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Stiff Starter Method</h3>
        
        <div className="space-y-4">
          <p className="text-white/80">
            Converting your starter to a stiff consistency (lower hydration) can extend its storage life in the refrigerator.
          </p>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Creating a Stiff Starter</h4>
            <ol className="list-decimal list-inside text-sm text-white/80 mt-2 space-y-2">
              <li>Take 50g of your active starter</li>
              <li>Mix with 100g flour and only 50g water (50% hydration)</li>
              <li>Knead until all flour is incorporated (will be like a firm dough)</li>
              <li>Let rise until doubled (may take longer than liquid starter)</li>
              <li>Refrigerate in a container with room for expansion</li>
            </ol>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Benefits of Stiff Starter</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Can be stored in refrigerator for 3-4 weeks without feeding</li>
              <li>Develops more acetic acid (tangy flavor) due to lower hydration</li>
              <li>Less prone to developing hooch</li>
              <li>Can be converted back to liquid starter when needed</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-amber-400 mb-4">Best Practices for Starter Preservation</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-lg text-white mb-2">Backup Strategy</h4>
            <p className="text-sm text-white/80">
              It's wise to maintain multiple backups of your starter using different preservation methods:
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2 space-y-1">
              <li>Keep your main starter in the refrigerator for regular use</li>
              <li>Store a dehydrated backup at home</li>
              <li>Keep another dehydrated backup off-site or with a friend</li>
              <li>Consider sharing your starter with other bakers as a living backup</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Reviving Neglected Starter</h4>
            <p className="text-sm text-white/80">
              If your starter has been neglected for an extended period:
            </p>
            <ol className="list-decimal list-inside text-sm text-white/80 mt-2 space-y-2">
              <li>Remove any discolored portions or mold (if present)</li>
              <li>Take a small amount from the center of the starter (1-2 tablespoons)</li>
              <li>Feed with equal parts flour and water (1:1:1 ratio)</li>
              <li>Discard and feed every 12 hours until activity returns</li>
              <li>May take 5-7 days to fully revive</li>
              <li>If no activity after a week, start from your backup</li>
            </ol>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">When to Discard</h4>
            <p className="text-sm text-white/80">
              Know when it's time to use your backup instead of trying to revive:
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2 space-y-1">
              <li>Visible mold growth (especially pink, orange, or black mold)</li>
              <li>Putrid or rotten smell (different from normal acidic smell)</li>
              <li>No signs of life after 7 days of regular feeding</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}